import { Controller } from "@hotwired/stimulus";

type Field = HTMLInputElement | HTMLSelectElement;

const DICE = /^\d+d\d+([+-]\d+)?$/;

// One race ability card: shows the fields of the chosen mechanic type, keeps the
// other types' fields disabled so they are not submitted, and writes the
// "Na ficha" line. Every sentence comes from the server (phrases value).
export default class extends Controller<HTMLElement> {
	static targets = ["name", "type", "group", "sheet", "sheetLine", "weaponName"];
	static values = { phrases: Object };

	declare readonly nameTarget: HTMLInputElement;
	declare readonly typeTarget: HTMLSelectElement;
	declare readonly groupTargets: HTMLElement[];
	declare readonly sheetTarget: HTMLElement;
	declare readonly sheetLineTarget: HTMLElement;
	declare readonly weaponNameTarget: HTMLInputElement;
	declare readonly phrasesValue: Record<string, string>;

	connect() {
		// Sortable moves the card, which reconnects it: only the first connect may hydrate.
		if (!this.element.dataset.hydrated) {
			this.fill(JSON.parse(this.element.dataset.mechanicConfig ?? "{}"));
			this.element.dataset.hydrated = "true";
		}
		this.update();
	}

	update() {
		const type = this.typeTarget.value;

		for (const group of this.groupTargets) {
			const active = group.dataset.type === type;
			group.hidden = !active;
			for (const field of this.fields(group, "input, select, textarea")) field.disabled = !active;
		}

		this.weaponNameTarget.placeholder = this.nameTarget.value;
		const [text, invalid] = this.describe(type);
		this.sheetTarget.textContent = text;
		this.sheetLineTarget.dataset.invalid = String(invalid);
		// A construction's summary is drawn by its own panel; this line only carries its errors.
		this.sheetLineTarget.hidden = type === "variable_construction" && !invalid;
	}

	// A saved ability opens with its values; only the saved type's fields take them.
	private fill(config: Record<string, unknown>) {
		const group = this.groupTargets.find((g) => g.dataset.type === this.typeTarget.value);
		if (!group) return;
		for (const field of this.fields(group, "[data-key]")) {
			const value = config[field.dataset.key ?? ""];
			if (field instanceof HTMLInputElement && field.type === "checkbox") {
				field.checked = Array.isArray(value) && value.includes(field.value);
			} else if (value !== undefined && value !== null) {
				field.value = String(value);
			}
		}
	}

	private fields(root: HTMLElement, selector: string) {
		return [...root.querySelectorAll<Field>(selector)];
	}

	private describe(type: string): [string, boolean] {
		const group = this.groupTargets.find((g) => g.dataset.type === type);
		const phrases = this.phrasesValue;
		const say = (key: string, vars: Record<string, string> = {}) => phrases[key].replace(/%\{(\w+)\}/g, (_, name) => vars[name] ?? "");
		if (!group) return [say("none"), false];

		const field = (key: string) => group.querySelector<Field>(`[data-key="${key}"]`);
		const value = (key: string) => (field(key)?.value ?? "").trim();
		const chosen = (key: string) => this.fields(group, `[data-key="${key}"]:checked`).map((input) => input.dataset.label ?? input.value);
		const word = (key: string) => (field(key) as HTMLSelectElement).selectedOptions[0]?.text.toLowerCase() ?? "";

		switch (type) {
			case "bonus_jp": {
				const saves = chosen("options");
				if (saves.length === 0) return [say("bonus_jp_none"), true];
				return [say(saves.length === 1 ? "bonus_jp_one" : "bonus_jp_many", { list: saves.join(` ${phrases.or} `) }), false];
			}
			case "rogue_talent_bonus": {
				const talents = chosen("talents");
				if (talents.length === 0) return [say("rogue_talent_bonus_none"), true];
				const last = talents.pop();
				return [say("rogue_talent_bonus", { list: talents.length ? `${talents.join(", ")} ${phrases.and} ${last}` : (last ?? "") }), false];
			}
			case "natural_weapon": {
				if (!DICE.test(value("damage"))) return [say("natural_weapon_error"), true];
				const bonus = Number(value("bonus_damage"));
				const range = value("throw_range");
				return [
					say("natural_weapon", {
						name: value("name") || this.nameTarget.value.trim(),
						damage: bonus ? `${value("damage")}${bonus > 0 ? "+" : ""}${bonus}` : value("damage"),
						type: word("damage_type"),
						size: word("weapon_size"),
						throw: range ? say("natural_weapon_throw", { value: range }) : "",
					}),
					false,
				];
			}
			case "variable_construction": {
				const rows = [...group.querySelectorAll<HTMLElement>("[data-race-construction-target='item']")];
				const picks = Number(value("selections_required"));
				if (!Number.isInteger(picks) || picks < 1 || picks > 10) return [say("construction_count_invalid"), true];
				if (rows.length === 0) return [say("construction_none"), true];
				for (const [index, row] of rows.entries()) {
					const name = row.querySelector<HTMLInputElement>("[data-key='name']")?.value.trim() ?? "";
					if (!name) return [say("construction_name_missing", { n: String(index + 1) }), true];
					const attack = row.querySelector<HTMLInputElement>("[data-role='attack']")?.checked;
					if (attack && !DICE.test(row.querySelector<HTMLInputElement>("[data-key='damage']")?.value.trim() ?? "")) {
						return [say("construction_damage_invalid", { name }), true];
					}
				}
				return [say(type, { count: value("selections_required"), total: String(rows.length) }), false];
			}
			case "bonus_damage":
				return [say(`bonus_damage_${value("condition")}`, { value: value("value") }), false];
			case "daily_ability":
				return [say(Number(value("uses_per_day")) === 1 ? "daily_ability_one" : "daily_ability_other", { value: value("uses_per_day") }), false];
			case "bonus_xp":
				return [say(type, { value: value("percentage") }), false];
			case "base_armor":
				return [say(type, { value: value("ac") }), false];
			case "armor_weight_modifier":
				return [say(type, { value: value("reduction") }), false];
			case "none":
				return [say("none"), false];
			default:
				return [say(type, { value: value("value") }), false];
		}
	}
}
